import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Loader2, CheckCircle2, AlertCircle, Mail, LogIn } from 'lucide-react';

/**
 * Landing page for the email-change confirmation link. Reads `?token=…` from the
 * URL, calls `/api/auth/email-change/confirm`, and then logs the user out so the
 * old JWT (now invalidated server-side via token_version) is cleared locally.
 */
export default function EmailChangeConfirmPage() {
    const [params] = useSearchParams();
    const token = params.get('token') || '';
    const navigate = useNavigate();
    const { logout } = useAuth();

    const [state, setState] = useState('loading'); // loading | success | error
    const [message, setMessage] = useState('');
    const [newEmail, setNewEmail] = useState('');

    useEffect(() => {
        if (!token) {
            setState('error');
            setMessage('Kein Bestätigungs-Token übergeben.');
            return;
        }
        (async () => {
            try {
                const res = await authApi.confirmEmailChange(token);
                setNewEmail(res.data.new_email || '');
                setMessage(res.data.message || 'E-Mail-Adresse erfolgreich geändert.');
                setState('success');
                // Lokale Sitzung aufräumen (Server-Token ist bereits invalide)
                try { await logout?.(); } catch { /* ignore */ }
            } catch (e) {
                setMessage(e?.response?.data?.detail || 'Der Link ist ungültig oder abgelaufen.');
                setState('error');
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12" data-testid="email-change-confirm-page">
            <Card className="max-w-md w-full">
                <CardContent className="p-8 text-center space-y-5">
                    {state === 'loading' && (
                        <>
                            <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />
                            <h1 className="text-xl font-semibold text-gray-900">E-Mail-Änderung wird bestätigt …</h1>
                            <p className="text-sm text-gray-500">Einen kleinen Augenblick bitte.</p>
                        </>
                    )}
                    {state === 'success' && (
                        <>
                            <div className="mx-auto h-14 w-14 rounded-full bg-green-100 flex items-center justify-center">
                                <CheckCircle2 className="h-8 w-8 text-green-600" />
                            </div>
                            <h1 className="text-xl font-semibold text-gray-900">E-Mail-Adresse geändert</h1>
                            <p className="text-sm text-gray-600">{message}</p>
                            {newEmail && (
                                <div className="p-3 rounded-xl bg-gray-50 border text-sm flex items-center gap-2 justify-center">
                                    <Mail className="h-4 w-4 text-primary" />
                                    <span className="font-medium text-gray-900">{newEmail}</span>
                                </div>
                            )}
                            <p className="text-xs text-gray-500">Alle vorherigen Sitzungen wurden aus Sicherheitsgründen abgemeldet. Bitte melden Sie sich mit der neuen Adresse neu an.</p>
                            <Button className="w-full" onClick={() => navigate('/login')} data-testid="go-to-login-btn">
                                <LogIn className="h-4 w-4 mr-2" /> Zum Login
                            </Button>
                        </>
                    )}
                    {state === 'error' && (
                        <>
                            <div className="mx-auto h-14 w-14 rounded-full bg-red-100 flex items-center justify-center">
                                <AlertCircle className="h-8 w-8 text-red-600" />
                            </div>
                            <h1 className="text-xl font-semibold text-gray-900">Bestätigung fehlgeschlagen</h1>
                            <p className="text-sm text-red-700">{message}</p>
                            <p className="text-xs text-gray-500">Sie können den Änderungs-Antrag im Portal erneut starten.</p>
                            <Button variant="outline" className="w-full" onClick={() => navigate('/login')} data-testid="go-to-login-btn">
                                Zum Login
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
